import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { jobApi, type JobSearchParams } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { Combobox } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { Segmented } from '@/components/ui/Toggle'
import { cn } from '@/lib/utils'
import type { JobFilterOption } from '@/types/api'

/**
 * The job filter bar: a search box (with "search in" and "exclude"), then one
 * chip per filter. A chip opens its panel under it on wide screens and as a
 * bottom sheet on phones. Every change applies at once; `onChange` gets the
 * whole filter set back.
 *
 * Used by the Dashboard (top matches and "Apply to all") and Browse jobs.
 */

type Option = { value: string; label: string }

/** The filters this bar edits: the job search minus paging. */
export type JobFilters = Omit<JobSearchParams, 'page' | 'pageSize'>

export const SEARCH_MODES: Option[] = [
  { value: 'title_description', label: 'Title + description' },
  { value: 'title_only', label: 'Title only' },
  { value: 'company', label: 'Company' },
]

const POSTED_WINDOWS: Option[] = [
  { value: '1', label: 'Last 24 hours' },
  { value: '3', label: 'Last 3 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
]

const WORKPLACES: Option[] = [
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'onsite', label: 'Onsite' },
]

const EMPLOYMENT_TYPES: Option[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
  { value: 'temporary', label: 'Temporary' },
]

const EXPERIENCE_CAPS: Option[] = [1, 2, 3, 5, 7, 10, 15].map((n) => ({
  value: String(n),
  label: `Up to ${n} year${n === 1 ? '' : 's'}`,
}))

const COUNTRY_PREFIX = 'country:'

/** How many filters are on, search words aside: what the badges and "Clear" count. */
export function countJobFilters(filters: JobFilters): number {
  return [
    filters.exclude,
    filters.sources?.length,
    filters.boards?.length,
    filters.locations?.length,
    filters.companies?.length || filters.excludeCompanies?.length,
    filters.workplaceTypes?.length,
    filters.employmentTypes?.length,
    filters.roles?.length,
    filters.seniorities?.length,
    filters.degreeLevel,
    filters.sponsorsVisa,
    filters.postedWithinDays,
    filters.maxExperienceYears,
    filters.easyApplyOnly || undefined,
  ].filter(Boolean).length
}

/** Everything the bar can turn off, so "Clear" keeps only the source choice and the search mode. */
export function clearedJobFilters(filters: JobFilters): JobFilters {
  return { searchMode: filters.searchMode, sources: filters.sources, boards: filters.boards }
}

/** Parts of the bar a caller can leave out (Auto Apply's API takes no exclude, search mode, degree or easy apply). */
export type JobFilterBarPart = 'exclude' | 'searchMode' | 'degreeLevel' | 'easyApplyOnly' | 'clear'

export function JobFilterBar({
  value,
  onChange,
  leading,
  searching,
  hide,
  placeholder,
}: {
  value: JobFilters
  onChange: (next: JobFilters) => void
  /** Extra chips at the start of the row (Browse jobs' Source). */
  leading?: ReactNode
  /** Shows the search box as busy. */
  searching?: boolean
  /** Leave these parts out. Default: show everything. */
  hide?: ReadonlyArray<JobFilterBarPart>
  /** The search box's placeholder. */
  placeholder?: string
}) {
  const hidden = (part: JobFilterBarPart) => Boolean(hide?.includes(part))
  const set = <K extends keyof JobFilters>(key: K, next: JobFilters[K]) => onChange({ ...value, [key]: next })
  const list = (items: string[] | undefined) => (items?.length ? items : undefined)

  const choicesQuery = useQuery({
    queryKey: ['job-filter-choices'],
    queryFn: jobApi.filterChoices,
    staleTime: Infinity,
  })
  const choices = choicesQuery.data
  for (const region of choices?.regions ?? []) stateLabels.set(region.value, region.label)

  const active = countJobFilters({ ...value, sources: undefined, boards: undefined }) + (value.q ? 1 : 0)
  const companyCount = (value.companies?.length ?? 0) + (value.excludeCompanies?.length ?? 0)

  return (
    <div className="space-y-2">
      <SearchBox
        value={value}
        onChange={onChange}
        searching={searching}
        showMode={!hidden('searchMode')}
        showExclude={!hidden('exclude')}
        placeholder={placeholder}
      />

      <div className="hscroll -mx-4 items-center gap-1.5 px-4 pb-0.5 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
        {leading}
        <FilterChip
          label="Date"
          count={value.postedWithinDays ? 1 : 0}
          summary={POSTED_WINDOWS.find((o) => o.value === String(value.postedWithinDays))?.label}
          onClear={() => set('postedWithinDays', undefined)}
        >
          {(close) => (
            <RadioList
              options={[{ value: '', label: 'Any time' }, ...POSTED_WINDOWS]}
              value={value.postedWithinDays ? String(value.postedWithinDays) : ''}
              onChange={(next) => {
                set('postedWithinDays', next ? Number(next) : undefined)
                close()
              }}
            />
          )}
        </FilterChip>
        <Divider />
        <FilterChip
          label="Location"
          icon={<PinIcon />}
          count={value.locations?.length ?? 0}
          width="w-[340px]"
          onClear={() => onChange({ ...value, locations: undefined, remoteIn: undefined })}
        >
          {() => (
            <LocationPanel
              locations={value.locations ?? []}
              remoteIn={value.remoteIn}
              onChange={(locations, remoteIn) => onChange({ ...value, locations: list(locations), remoteIn })}
            />
          )}
        </FilterChip>
        <FilterChip
          label="Workplace"
          count={value.workplaceTypes?.length ?? 0}
          onClear={() => set('workplaceTypes', undefined)}
        >
          {() => (
            <CheckList
              options={WORKPLACES}
              values={value.workplaceTypes ?? []}
              onChange={(next) => set('workplaceTypes', list(next))}
            />
          )}
        </FilterChip>
        <FilterChip
          label="Companies"
          icon={<BuildingIcon />}
          count={companyCount}
          width="w-[340px]"
          onClear={() => onChange({ ...value, companies: undefined, excludeCompanies: undefined })}
        >
          {() => (
            <CompaniesPanel
              only={value.companies ?? []}
              hidden={value.excludeCompanies ?? []}
              onChange={(only, hidden) => onChange({ ...value, companies: list(only), excludeCompanies: list(hidden) })}
            />
          )}
        </FilterChip>
        <Divider />
        {!hidden('degreeLevel') && (
          <FilterChip
            label="Degree level"
            count={value.degreeLevel ? 1 : 0}
            summary={choices?.degreeLevels.find((o) => o.value === value.degreeLevel)?.label}
            onClear={() => set('degreeLevel', undefined)}
          >
            {(close) => (
              <>
                <PanelHint>Your highest degree. Hides jobs that ask for more.</PanelHint>
                <RadioList
                  options={[{ value: '', label: 'Any' }, ...(choices?.degreeLevels ?? [])]}
                  value={value.degreeLevel ?? ''}
                  onChange={(next) => {
                    set('degreeLevel', next || undefined)
                    close()
                  }}
                />
              </>
            )}
          </FilterChip>
        )}
        <FilterChip
          label="Max experience"
          count={value.maxExperienceYears ? 1 : 0}
          summary={value.maxExperienceYears ? `≤ ${value.maxExperienceYears} yrs` : undefined}
          onClear={() => set('maxExperienceYears', undefined)}
        >
          {(close) => (
            <>
              <PanelHint>Hides jobs that ask for more years than this.</PanelHint>
              <RadioList
                options={[{ value: '', label: 'Any' }, ...EXPERIENCE_CAPS]}
                value={value.maxExperienceYears ? String(value.maxExperienceYears) : ''}
                onChange={(next) => {
                  set('maxExperienceYears', next ? Number(next) : undefined)
                  close()
                }}
              />
            </>
          )}
        </FilterChip>
        <ToggleChip
          label="Sponsors visa"
          active={value.sponsorsVisa === true}
          onClick={() => set('sponsorsVisa', value.sponsorsVisa === true ? undefined : true)}
        />
        <Divider />
        <FilterChip label="Role" count={value.roles?.length ?? 0} width="w-[280px]" onClear={() => set('roles', undefined)}>
          {() => (
            <CheckList options={choices?.roles ?? []} values={value.roles ?? []} onChange={(next) => set('roles', list(next))} />
          )}
        </FilterChip>
        <FilterChip
          label="Seniority"
          count={value.seniorities?.length ?? 0}
          width="w-[260px]"
          onClear={() => set('seniorities', undefined)}
        >
          {() => (
            <CheckList
              options={choices?.seniorities ?? []}
              values={value.seniorities ?? []}
              onChange={(next) => set('seniorities', list(next))}
            />
          )}
        </FilterChip>
        <FilterChip
          label="Employment type"
          count={value.employmentTypes?.length ?? 0}
          onClear={() => set('employmentTypes', undefined)}
        >
          {() => (
            <CheckList
              options={EMPLOYMENT_TYPES}
              values={value.employmentTypes ?? []}
              onChange={(next) => set('employmentTypes', list(next))}
            />
          )}
        </FilterChip>
        {!hidden('easyApplyOnly') && (
          <ToggleChip
            label="Easy apply"
            active={Boolean(value.easyApplyOnly)}
            onClick={() => set('easyApplyOnly', value.easyApplyOnly ? undefined : true)}
          />
        )}
        {active > 0 && !hidden('clear') && (
          <button
            type="button"
            onClick={() => onChange(clearedJobFilters(value))}
            className="tap ml-auto inline-flex h-8 shrink-0 items-center gap-1 rounded-pill px-2.5 text-[13px] text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
          >
            <CloseIcon />
            Clear <span className="num">({active})</span>
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Search box
// ---------------------------------------------------------------------------

/** Words apply after a short pause or on Enter. */
function SearchBox({
  value,
  onChange,
  searching,
  showMode = true,
  showExclude = true,
  placeholder = 'Search by title or keyword…',
}: {
  value: JobFilters
  onChange: (next: JobFilters) => void
  searching?: boolean
  showMode?: boolean
  showExclude?: boolean
  placeholder?: string
}) {
  const [query, setQuery] = useState(value.q ?? '')
  const [exclude, setExclude] = useState(value.exclude ?? '')
  // Follow outside changes ("Clear"), not our own echoes.
  useEffect(() => setQuery(value.q ?? ''), [value.q])
  useEffect(() => setExclude(value.exclude ?? ''), [value.exclude])

  const latest = useRef({ value, onChange })
  latest.current = { value, onChange }
  const commit = (q: string, ex: string) => {
    const current = latest.current.value
    const nextQ = q.trim() || undefined
    const nextEx = ex.trim() || undefined
    if (nextQ === (current.q || undefined) && nextEx === (current.exclude || undefined)) return
    latest.current.onChange({ ...current, q: nextQ, exclude: nextEx })
  }
  useEffect(() => {
    const timer = window.setTimeout(() => commit(query, exclude), 450)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, exclude])

  return (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault()
        commit(query, exclude)
      }}
      className="space-y-2"
    >
      <div className="flex items-stretch gap-2 rounded-card border border-line bg-surface p-1.5 shadow-card">
        {showMode && (
          <div className="hidden w-[176px] shrink-0 md:block">
            <Combobox
              appearance="box"
              options={SEARCH_MODES}
              value={value.searchMode ?? 'title_description'}
              onChange={(mode) => onChange({ ...value, searchMode: mode })}
              searchable={false}
            />
          </div>
        )}
        <label className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
            {searching ? <Spinner /> : <SearchIcon />}
          </span>
          <input
            type="search"
            enterKeyHint="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            aria-label="Search jobs"
            className="h-11 w-full rounded-field bg-transparent pl-10 pr-3 text-[15px] text-ink outline-none placeholder:text-ink-faint"
          />
        </label>
      </div>
      {showExclude && (
        <label className="flex h-10 items-center gap-2 rounded-field bg-surface-muted px-3 text-[13px]">
          <span className="shrink-0 font-medium text-ink-muted">Exclude</span>
          <input
            value={exclude}
            onChange={(event) => setExclude(event.target.value)}
            placeholder="Hide jobs mentioning… (comma separated)"
            aria-label="Exclude keywords"
            className="min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-ink-faint"
          />
        </label>
      )}
    </form>
  )
}

// ---------------------------------------------------------------------------
// Location
// ---------------------------------------------------------------------------

const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null

export function countryLabel(code: string): string {
  try {
    return regionNames?.of(code.toUpperCase()) ?? code
  } catch {
    return code
  }
}

/** A chosen place as people read it: "India", "Pune". */
const stateLabels = new Map<string, string>()
export function placeLabel(value: string): string {
  if (value.startsWith('city:')) { const [, country, ...name] = value.split(':'); return `${name.join(':')}, ${country}` }
  if (value.startsWith('state:')) return stateLabels.get(value) ?? `${value.split(':')[2]}, ${value.split(':')[1]} (state)`
  return value.startsWith(COUNTRY_PREFIX) ? countryLabel(value.slice(COUNTRY_PREFIX.length)) : value
}

function LocationPanel({
  locations,
  remoteIn,
  onChange,
}: {
  locations: string[]
  remoteIn: string | undefined
  onChange: (locations: string[], remoteIn: string | undefined) => void
}) {
  const [text, setText] = useState('')
  const q = useDebounced(text.trim(), 250)

  // Every country with jobs, most first: the "limit to" default and the remote picker.
  const countriesQuery = useQuery({
    queryKey: ['job-filter-options', 'locations', 'countries'],
    queryFn: () => jobApi.filterOptions({ field: 'locations', limit: 50 }),
    staleTime: 120_000,
  })
  const countries = useMemo(
    () => (countriesQuery.data ?? []).filter((o) => o.kind === 'country'),
    [countriesQuery.data],
  )
  const topCountry = countries[0]?.value.slice(COUNTRY_PREFIX.length)
  const [limitOn, setLimitOn] = useState(true)
  const limitTo = limitOn ? topCountry : undefined

  const optionsQuery = useQuery({
    queryKey: ['job-filter-options', 'locations', q, limitTo ?? null],
    queryFn: () => jobApi.filterOptions({ field: 'locations', q: q || undefined, country: limitTo, limit: 20 }),
    enabled: !limitOn || Boolean(topCountry),
    staleTime: 60_000,
  })

  for (const option of optionsQuery.data ?? []) if (option.kind === 'state') stateLabels.set(option.value, option.label)

  const toggle = (place: string) =>
    onChange(
      locations.includes(place) ? locations.filter((p) => p !== place) : [...locations.filter((p) => place.startsWith(COUNTRY_PREFIX) ? p.startsWith(COUNTRY_PREFIX) : !p.startsWith(COUNTRY_PREFIX)), place],
      remoteIn,
    )
  const typed = text.trim()
  const offerTyped =
    typed.length > 1 &&
    !locations.some((p) => p.toLowerCase() === typed.toLowerCase()) &&
    !(optionsQuery.data ?? []).some((o) => o.label.toLowerCase() === typed.toLowerCase())

  return (
    <div className="flex max-h-[min(70dvh,520px)] flex-col">
      {topCountry && (
        <label className="flex cursor-pointer items-center justify-between gap-3 px-3 pb-2 pt-1 text-[13.5px] text-ink">
          Limit to {countryLabel(topCountry)}
          <CheckBox checked={limitOn} onChange={setLimitOn} label={`Limit to ${countryLabel(topCountry)}`} />
        </label>
      )}
      <div className="px-3">
        <SearchField value={text} onChange={setText} placeholder="Search locations…" />
        {limitTo && (
          <p className="mt-2 rounded-[8px] bg-surface-muted px-2.5 py-1.5 text-[12px] text-ink-muted">
            Showing locations in {countryLabel(limitTo)}
          </p>
        )}
      </div>

      {locations.length > 0 && (
        <div className="mt-2 border-t border-line px-3 pt-2">
          <p className="label-eyebrow mb-1.5">
            Selected (<span className="num">{locations.length}</span>)
          </p>
          <div className="flex flex-wrap gap-1.5">
            {locations.map((place) => (
              <SelectedChip key={place} label={placeLabel(place)} onRemove={() => toggle(place)} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 min-h-0 flex-1 overflow-y-auto border-t border-line py-1">
        <p className="label-eyebrow px-3 pb-1 pt-2">
          {q ? 'Results' : limitTo ? `Popular in ${countryLabel(limitTo)}` : 'Popular'}
        </p>
        {offerTyped && (
          <OptionRow checked={false} onToggle={() => { toggle(typed); setText('') }} label={`“${typed}”`} meta="Any match" />
        )}
        <OptionRows
          query={optionsQuery}
          selected={locations}
          onToggle={toggle}
          metaFor={(o) => (o.kind === 'country' ? 'Country' : o.kind === 'state' ? 'State / region' : 'City')}
        />
      </div>

      {countries.length > 0 && (
        <div className="flex items-center gap-2 border-t border-line px-3 pt-2.5 text-[13px] text-ink">
          <CheckBox
            checked={Boolean(remoteIn)}
            onChange={(on) => onChange(locations, on ? remoteIn ?? topCountry : undefined)}
            label="Also include remote jobs"
          />
          <span className="min-w-0 flex-1">Also include remote jobs in</span>
          <select
            value={remoteIn ?? topCountry ?? ''}
            onChange={(event) => onChange(locations, event.target.value)}
            aria-label="Country for remote jobs"
            className="field-box h-8 w-[130px] truncate py-0 text-[12.5px]"
          >
            {countries.map((c) => {
              const code = c.value.slice(COUNTRY_PREFIX.length)
              return (
                <option key={code} value={code}>
                  {c.label}
                </option>
              )
            })}
          </select>
        </div>
      )}
      {remoteIn && locations.length === 0 && (
        <p className="px-3 pt-1.5 text-[12px] text-ink-muted">Pick a place too: remote jobs are added to it.</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

function CompaniesPanel({
  only,
  hidden,
  onChange,
}: {
  only: string[]
  hidden: string[]
  onChange: (only: string[], hidden: string[]) => void
}) {
  const [mode, setMode] = useState<'only' | 'hide'>(hidden.length && !only.length ? 'hide' : 'only')
  const [text, setText] = useState('')
  const q = useDebounced(text.trim(), 250)
  const optionsQuery = useQuery({
    queryKey: ['job-filter-options', 'companies', q],
    queryFn: () => jobApi.filterOptions({ field: 'companies', q: q || undefined, limit: 20 }),
    staleTime: 60_000,
  })

  const current = mode === 'only' ? only : hidden
  const toggle = (name: string) => {
    const next = current.includes(name) ? current.filter((c) => c !== name) : [...current, name]
    // A company is either kept or hidden, not both.
    if (mode === 'only') onChange(next, hidden.filter((c) => c !== name))
    else onChange(only.filter((c) => c !== name), next)
  }
  const typed = text.trim()
  const offerTyped =
    typed.length > 1 &&
    !current.some((c) => c.toLowerCase() === typed.toLowerCase()) &&
    !(optionsQuery.data ?? []).some((o) => o.label.toLowerCase() === typed.toLowerCase())

  return (
    <div className="flex max-h-[min(70dvh,480px)] flex-col">
      <div className="space-y-2 px-3 pt-1">
        <Segmented
          fullWidth
          size="sm"
          aria-label="Show or hide companies"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'only', label: 'Only these', count: only.length || undefined },
            { value: 'hide', label: 'Hide', count: hidden.length || undefined },
          ]}
        />
        <SearchField
          value={text}
          onChange={setText}
          placeholder={mode === 'only' ? 'Search companies to show…' : 'Search companies to hide…'}
        />
      </div>

      {current.length > 0 && (
        <div className="mt-2 border-t border-line px-3 pt-2">
          <p className="label-eyebrow mb-1.5">{mode === 'only' ? 'Showing only' : 'Hiding'}</p>
          <div className="flex flex-wrap gap-1.5">
            {current.map((name) => (
              <SelectedChip key={name} label={name} onRemove={() => toggle(name)} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 min-h-0 flex-1 overflow-y-auto border-t border-line py-1">
        <p className="label-eyebrow px-3 pb-1 pt-2">{q ? 'Results' : 'Popular'}</p>
        {offerTyped && (
          <OptionRow checked={false} onToggle={() => { toggle(typed); setText('') }} label={`“${typed}”`} meta="Name contains" />
        )}
        <OptionRows query={optionsQuery} selected={current} onToggle={toggle} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Panel pieces
// ---------------------------------------------------------------------------

function OptionRows({
  query,
  selected,
  onToggle,
  metaFor,
}: {
  query: { data?: JobFilterOption[]; isLoading: boolean; isError: boolean }
  selected: string[]
  onToggle: (value: string) => void
  metaFor?: (option: JobFilterOption) => string
}) {
  if (query.isLoading) return <p className="px-3 py-2 text-[13px] text-ink-muted">Loading…</p>
  if (query.isError) return <p className="px-3 py-2 text-[13px] text-danger">Could not load the choices.</p>
  if (!query.data?.length) return <p className="px-3 py-2 text-[13px] text-ink-muted">Nothing matches.</p>
  return (
    <>
      {query.data.map((option) => (
        <OptionRow
          key={option.value}
          checked={selected.includes(option.value)}
          onToggle={() => onToggle(option.value)}
          label={option.label}
          meta={metaFor ? metaFor(option) : undefined}
          count={option.count}
        />
      ))}
    </>
  )
}

function OptionRow({
  checked,
  onToggle,
  label,
  meta,
  count,
}: {
  checked: boolean
  onToggle: () => void
  label: string
  meta?: string
  count?: number
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className="flex min-h-[40px] w-full items-center gap-2.5 px-3 py-1.5 text-left text-[14px] text-ink transition-colors hover:bg-surface-muted"
    >
      <Box checked={checked} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count != null && <span className="num text-[11.5px] text-ink-faint">{count}</span>}
      {meta && <span className="shrink-0 text-[11.5px] text-ink-faint">{meta}</span>}
    </button>
  )
}

function CheckList({
  options,
  values,
  onChange,
}: {
  options: Option[]
  values: string[]
  onChange: (values: string[]) => void
}) {
  if (!options.length) return <p className="px-3 py-2 text-[13px] text-ink-muted">Loading…</p>
  return (
    <div className="py-0.5">
      {options.map((option) => {
        const checked = values.includes(option.value)
        return (
          <OptionRow
            key={option.value}
            checked={checked}
            label={option.label}
            onToggle={() => onChange(checked ? values.filter((v) => v !== option.value) : [...values, option.value])}
          />
        )
      })}
    </div>
  )
}

function RadioList({
  options,
  value,
  onChange,
}: {
  options: Option[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div role="radiogroup" className="py-0.5">
      {options.map((option) => {
        const checked = option.value === value
        return (
          <button
            key={option.value || 'any'}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(option.value)}
            className="flex min-h-[40px] w-full items-center gap-2.5 px-3 py-1.5 text-left text-[14px] text-ink transition-colors hover:bg-surface-muted"
          >
            <span
              className={cn(
                'grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                checked ? 'border-ink' : 'border-line-strong',
              )}
              aria-hidden="true"
            >
              {checked && <span className="h-2 w-2 rounded-full bg-ink" />}
            </span>
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function PanelHint({ children }: { children: ReactNode }) {
  return <p className="px-3 pb-1 pt-1 text-[12px] leading-snug text-ink-muted">{children}</p>
}

function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <label className="relative block">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
        <SearchIcon />
      </span>
      <input
        autoFocus
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="field-box h-10 w-full pl-9 text-[14px]"
      />
    </label>
  )
}

function SelectedChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex h-7 max-w-full items-center gap-1 rounded-[7px] bg-surface-muted pl-2 pr-1 text-[12.5px] text-ink">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="grid h-5 w-5 shrink-0 place-items-center rounded-[5px] text-ink-muted hover:bg-line hover:text-ink"
      >
        <CloseIcon />
      </button>
    </span>
  )
}

function Box({ checked }: { checked: boolean }) {
  return (
    <span
      className={cn(
        'grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border transition-colors',
        checked ? 'border-ink bg-ink text-oncolor' : 'border-line-strong bg-surface',
      )}
      aria-hidden="true"
    >
      {checked && <CheckIcon />}
    </span>
  )
}

function CheckBox({ checked, onChange, label }: { checked: boolean; onChange: (checked: boolean) => void; label: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className="tap shrink-0">
      <Box checked={checked} />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

const CHIP = 'inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap border text-[13px] transition-colors'
const CHIP_IDLE = 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink'
const CHIP_ON = 'border-ink bg-ink text-oncolor'

/**
 * A filter chip and its panel. Off: "Label ⌄". On: "Label [n] ×", where ×
 * clears just this filter. The panel is a dropdown from md up and a bottom
 * sheet on phones (where the chip row scrolls and would clip a dropdown).
 */
function FilterChip({
  label,
  icon,
  count,
  summary,
  width = 'w-[240px]',
  onClear,
  children,
}: {
  label: string
  icon?: ReactNode
  count: number
  /** For single-choice filters: the choice, shown on the chip. */
  summary?: string
  width?: string
  onClear: () => void
  children: (close: () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [alignRight, setAlignRight] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const phone = useIsPhone()
  const on = count > 0
  const close = () => setOpen(false)

  useEffect(() => {
    if (!open || phone) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, phone])

  const toggle = () => {
    const rect = anchor.current?.getBoundingClientRect()
    if (rect) setAlignRight(rect.left > window.innerWidth - 360)
    setOpen((isOpen) => !isOpen)
  }

  return (
    <div ref={anchor} className="relative shrink-0">
      <div className={cn(CHIP, 'rounded-pill', on ? CHIP_ON : CHIP_IDLE)}>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-haspopup="dialog"
          className={cn('tap inline-flex h-full items-center gap-1.5 pl-3', on ? 'pr-1' : 'pr-2.5')}
        >
          {icon}
          <span className="max-w-[180px] truncate">{on && summary ? `${label}: ${summary}` : label}</span>
          {on && !summary && (
            <span className="num grid h-[18px] min-w-[18px] place-items-center rounded-[5px] bg-oncolor/20 px-1 text-[11px] font-semibold">
              {count}
            </span>
          )}
          {!on && <ChevronDownIcon open={open} />}
        </button>
        {on && (
          <button
            type="button"
            onClick={() => {
              onClear()
              close()
            }}
            aria-label={`Clear ${label}`}
            className="tap mr-1 grid h-6 w-6 place-items-center rounded-pill hover:bg-oncolor/20"
          >
            <CloseIcon />
          </button>
        )}
      </div>

      {open && !phone && (
        <>
          <div className="fixed inset-0 z-30" onClick={close} aria-hidden="true" />
          <div
            role="dialog"
            aria-label={label}
            className={cn(
              'absolute z-40 mt-1.5 overflow-hidden rounded-[12px] border border-line bg-surface py-2 shadow-float animate-fade-in',
              alignRight ? 'right-0' : 'left-0',
              width,
            )}
          >
            {children(close)}
          </div>
        </>
      )}
      {open && phone && (
        <Sheet
          open
          side="bottom"
          onClose={close}
          title={label}
          footer={
            <Button className="flex-1" onClick={close}>
              Done
            </Button>
          }
        >
          <div className="-mx-3">{children(close)}</div>
        </Sheet>
      )}
    </div>
  )
}

function ToggleChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(CHIP, 'tap rounded-pill px-3', active ? CHIP_ON : CHIP_IDLE)}
    >
      {active && <CheckIcon />}
      {label}
    </button>
  )
}

function Divider() {
  return <span className="mx-0.5 hidden h-4 w-px shrink-0 bg-line md:block" aria-hidden="true" />
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const PHONE = '(max-width: 767px)'

function useIsPhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE).matches)
  useEffect(() => {
    const media = window.matchMedia(PHONE)
    const update = () => setPhone(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  return phone
}

function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])
  return settled
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SearchIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.5 10.5 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 12.5s4-3.6 4-6.8A4 4 0 0 0 3 5.7c0 3.2 4 6.8 4 6.8Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="7" cy="5.7" r="1.4" fill="currentColor" />
    </svg>
  )
}

function BuildingIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 12.5V2.5h6v10M9 6h2.5v6.5M1.5 12.5h11M5 5h2M5 7.5h2M5 10h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m2.5 6.2 2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m3 3 6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ChevronDownIcon({ open }: { open?: boolean }) {
  return (
    <svg className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m3 4.5 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}
